import React, { useEffect } from 'react';
import './ChatbotPlugin.css';
import { useChatbotState } from './hooks/useChatbotState';
import { useChatbotFunctions } from './hooks/useChatbotFunctions';
import { MessageContent } from './components/MessageContent';
import { DEFAULT_CONFIG } from './config/constants';

// ==================== MAIN COMPONENT ====================
const ChatbotPlugin = (props = {}) => {
  // Merge props with default config
  const config = {
    ...DEFAULT_CONFIG,
    ...props,
    theme: { ...DEFAULT_CONFIG.theme, ...props.theme }
  };

  // Initialize state
  const state = useChatbotState();
  const {
    isOpen, setIsOpen,
    messages, setMessages,
    inputText, setInputText,
    isLoading,
    currentFlow,
    messagesEndRef,
    chatContainerRef,
    completedTransactions
  } = state;

  // Initialize functions
  const { addMessage, handleMenuAction, sendMessage, generateMenuOptions } = useChatbotFunctions(state, config);

  // Initialize chat with welcome message and main menu
  useEffect(() => {
    const welcomeMsg = {
      text: config.welcomeMessage,
      sender: 'bot',
      timestamp: new Date().toISOString(),
      type: 'text'
    };

    const menuMsg = {
      text: "Please select a service",
      sender: 'bot',
      timestamp: new Date().toISOString(),
      type: 'menu',
      options: generateMenuOptions()
    };

    setMessages([welcomeMsg, menuMsg]);
  }, [config.welcomeMessage, completedTransactions.length]);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chatContainerRef.current) {
        if (window.innerWidth < 768) {
          chatContainerRef.current.style.maxHeight = '70vh';
        } else {
          chatContainerRef.current.style.maxHeight = config.maxHeight;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [config.maxHeight]);

  // Event handlers
  const toggleChat = () => setIsOpen(!isOpen);
  const handleInputChange = (e) => setInputText(e.target.value);
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputText, setInputText);
    }
  };

  return (
    <div className="chatbot-container" style={config.position}>
      {/* Chatbot toggle button */}
      <button
        className="chatbot-toggle"
        onClick={toggleChat}
        style={{ backgroundColor: config.theme.primary }}
      >
        {isOpen ? <span>✕</span> : <span>💬</span>}
      </button>

      {/* Chatbot dialog */}
      {isOpen && (
        <div
          className="chatbot-dialog"
          style={{
            backgroundColor: config.theme.background,
            color: config.theme.text,
            borderColor: config.theme.primary
          }}
        >
          {/* Header */}
          <div
            className="chatbot-header"
            style={{ backgroundColor: config.theme.primary, color: '#fff' }}
          >
            <h3>{config.botName}</h3>
          </div>

          {/* Messages container */}
          <div className="chatbot-messages" ref={chatContainerRef}>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`chatbot-message ${msg.sender === 'bot' ? 'bot-message' : 'user-message'} ${msg.type === 'error' ? 'error-message' : ''} ${msg.type === 'success' ? 'success-message' : ''}`}
                style={{
                  backgroundColor: msg.sender === 'bot'
                    ? (msg.type === 'error' ? config.theme.error
                      : msg.type === 'success' ? config.theme.success
                        : '#f1f3f4')
                    : config.theme.primary,
                  color: msg.sender === 'bot'
                    ? (msg.type === 'error' || msg.type === 'success' ? '#fff' : config.theme.text)
                    : '#fff'
                }}
              >
                <MessageContent
                  message={msg}
                  handleMenuAction={handleMenuAction}
                  theme={config.theme}
                />
                <div className="message-timestamp">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-message bot-message loading-message">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            {/* Input area - only show for support flow */}
            {currentFlow === 'support' && (
              <div className="chatbot-input">
                <textarea
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder={config.placeholder}
                  rows={1}
                  style={{ borderColor: config.theme.primary }}
                />
                <button
                  onClick={() => sendMessage(inputText, setInputText)}
                  disabled={isLoading || !inputText.trim()}
                  style={{
                    backgroundColor: config.theme.primary,
                    opacity: (isLoading || !inputText.trim()) ? 0.6 : 1
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="white" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatbotPlugin;